/* eslint-disable no-undef */
import firebase from "firebase/compat/app";
// import "firebase/compat/analytics";
// import "firebase/compat/storage";
// import "firebase/compat/firestore";

import _ from "underscore";
import moment from "moment";
import { getRecoil } from "recoil-nexus";
import { getAPIBasePath } from "./StripeData";
import { getUserIdToken } from "../utils/auth";
import { exerciseCache } from "../utils/ExerciseCache";

import organizationIdState from "../atoms/organizationIdSelector";
import { nanoid } from "nanoid";
import { consola } from "consola";

export const ViewMode = {
  list: "list",
  list_details: "list_details",
  details: "details",
};

export const pages = {
  dashboard: "dashboard",
  reports: "reports",
  clients: "clients",
  coaches: "coaches",
  protocols: "protocols",
  training_plans_v2: "training_plans_v2",
  messages: "messages",
  shared_videos: "shared_videos",
  exercises: "exercises",
  recipes: "recipes",
  library: "library",
  learn: "learn",
  admin: "admin",
  developers: "developers",
};

export const CoachStatus = {
  active: "active",
  disabled: "disabled",
};

export const QuestionType = {
  weekly: "weekly",
  daily: "daily",
  progress: "progress",
};

export const DataType = {
  multiline: "multiline",
  number: "number",
  rating: "rating",
  scale: "scale",
  yesno: "yesno",
};

export const WorkoutStatus = {
  complete: "complete",
  incomplete: "incomplete",
};

export const DataTypeString = {
  multiline: "Multiline",
  number: "Number",
  rating: "Rating",
  scale: "Scale",
  yesno: "Yes/No",
};

export const Difficulty = {
  beginner: "beginner",
  intermediate: "intermediate",
  advanced: "advanced",
  expert: "expert",
};

export const DaysOfWeek = {
  monday: "monday",
  tuesday: "tuesday",
  wednesday: "wednesday",
  thursday: "thursday",
  friday: "friday",
  saturday: "saturday",
  sunday: "sunday",
};

export const DaysOfWeekString = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

export const ago = (date) => {
  return moment(date).calendar(null, {
    sameDay: "[Today]",
    nextDay: "[Tomorrow]",
    nextWeek: "dddd",
    lastDay: "[Yesterday]",
    lastWeek: "[Last] dddd",
    sameElse: function (now) {
      const diff = now.diff(this, "days");
      const result = `[${diff} days ago]`;
      return result;
    },
  });
};

export const convertHeightToCm = (height) => {
  if (!height || height.trim() === "") {
    return 0;
  }
  if (height.indexOf("'") === -1 && height.indexOf("’") === -1) {
    const parsed = parseFloat(height);
    if (!isNaN(parsed)) {
      return parseFloat(height);
    }
  }

  let heightClean = height.replace(/\s/g, "");
  heightClean = heightClean.replace(/’/g, "'");
  // check if height is in cm
  if (heightClean.indexOf("cm") > -1) {
    return parseFloat(heightClean.replace("cm", ""));
  }
  // check if height is float
  if (heightClean.indexOf(".") > -1) {
    return parseFloat(heightClean);
  }

  const [feet, inches] = heightClean.split("'");
  const totalInches = parseInt(feet) * 12 + parseInt(inches);
  return totalInches * 2.54;
};

export const convertWeightToKg = (weight) => {
  return Math.round((weight * 0.453592 + Number.EPSILON) * 100) / 100;
};

export const getAgeFromBirthday = (birthday) => {
  if (!birthday) {
    return 0;
  }

  const today = new Date();
  const birthDate = new Date(birthday);
  if (isNaN(birthDate.getTime())) {
    consola.error(new Error("Invalid date"));
    return 0;
  }
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const getOpenAIResponse = async (prompt, temperature = 0.5) => {
  if (!prompt) {
    consola.error(new Error("Incorrect parameters"));
  }

  const messages = {
    messages: [
      {
        role: "user",
        content: prompt,
      },
    ],
    temperature,
    model: "gpt-4",
  };

  const path = getAPIBasePath();
  const fullPath = `${path}/chatgpt`;

  consola.debug("++++++ READ -> GlobalData: getOpenAIResponse", fullPath);

  const response = await fetch(fullPath, {
    method: "POST",
    body: JSON.stringify(messages),
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (response.status === 200) {
    const jsonData = await response.json();
    consola.debug("++++++ READ -> GlobalData: getOpenAIResponse", jsonData);
    // Transform the response to match the old format for backwards compatibility
    return {
      status: jsonData.status,
      response: jsonData.response[0].message.content,
    };
  }
  consola.error(new Error("Error getting openai response"));
};

export const getChatGPTResponse = async (messages) => {
  if (!messages) {
    consola.error(new Error("Incorrect parameters"));
  }

  const path = getAPIBasePath();
  const sendBody = JSON.stringify(messages, null, 2);

  consola.debug("++++++ READ -> GlobalData: getChatGPTResponse", sendBody);

  // Create an AbortController for timeout handling
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 480000); // 8 minutes timeout (less than function timeout)

  try {
    const token = await getUserIdToken();
    const response = await fetch(`${path}/chatgpt`, {
      method: "POST",
      body: sendBody,
      mode: "cors", // no-cors, *cors, same-origin
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (response.status === 200) {
      const jsonData = await response.json();
      return jsonData;
    }
    let errorPayload = null;
    try {
      errorPayload = await response.json();
    } catch (_e) {
      // ignore
    }
    if (response.status === 403 || errorPayload?.code === "FEATURE_LOCKED") {
      const err = new Error(
        errorPayload?.error ||
          "This AI feature requires a Pro plan. Upgrade to unlock.",
      );
      err.code = "FEATURE_LOCKED";
      err.status = 403;
      throw err;
    }
    consola.error(new Error("Error getting openai response"));
    throw new Error(
      errorPayload?.error || `AI request failed (${response.status})`,
    );
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      consola.error(new Error("Request timeout - ChatGPT request took too long"));
      throw new Error("Request timeout - please try again with a smaller request");
    }
    throw error;
  }
};

/**
 * Authenticated JSON request to coach API (BigQuery-backed exercise endpoints).
 */
async function exercisesBigQueryRequest(path, options = {}) {
  const token = await getUserIdToken();
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const response = await fetch(`${getAPIBasePath()}${normalizedPath}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(json.error || `HTTP ${response.status}`);
    err.status = response.status;
    throw err;
  }
  return json;
}

/**
 * Loads global + org custom exercises via BigQuery API (replaces removed batched Firestore reads).
 */
async function getAllExercises({ filter, organizationId } = {}) {
  const orgId = organizationId || getRecoil(organizationIdState);
  if (!orgId) {
    consola.error(new Error("getAllExercises: missing organizationId"));
    return [];
  }
  const params = new URLSearchParams({
    limit: "10000",
    offset: "0",
    organization_id: orgId,
  });
  const result = await exercisesBigQueryRequest(`/exercises-catalog-v2?${params}`);
  let exercises = result.data?.exercises || [];

  exercises = exercises.map((ex) => ({
    ...ex,
    mainMuscleGroups:
      ex.mainMuscleGroups || ex.main_muscle_groups || [],
    secondaryMuscleGroups:
      ex.secondaryMuscleGroups || ex.secondary_muscle_groups || [],
  }));

  if (filter && filter !== "all" && String(filter).length > 0) {
    exercises = exercises.filter((exercise) => {
      const main = exercise.mainMuscleGroups || [];
      if (main.length > 0) return main.includes(filter);
      return (exercise.muscleGroups || []).includes(filter);
    });
  }

  return exercises;
}

async function getExerciseById(id) {
  if (!id) return null;
  try {
    const result = await exercisesBigQueryRequest(
      `/exercises-catalog-v2/${encodeURIComponent(id)}`,
    );
    const ex = result.data?.exercise || null;
    if (ex?.id) {
      exerciseCache.set(ex.id, ex);
    }
    return ex;
  } catch (e) {
    if (e.status === 404) return null;
    consola.error("getExerciseById:", e);
    return null;
  }
}

async function updateExerciseInBatches(exercise) {
  if (!exercise?.id) {
    consola.error(new Error("updateExerciseInBatches: missing exercise.id"));
    return;
  }
  await exercisesBigQueryRequest(
    `/exercises-catalog-v2/${encodeURIComponent(exercise.id)}`,
    {
      method: "PUT",
      body: JSON.stringify(exercise),
    },
  );
}

const GlobalData = {
  // Static data methods removed - now using BigQuery via useStaticDataBigQuery hook:
  // - getMuscleGroups: use useMuscleGroupsBigQuery()
  // - getEquipment: use useEquipmentBigQuery()
  // - getEquipmentKey: use useEquipmentBigQuery()
  // - getDifficultyKey: use useDifficultyBigQuery()
  // - getMuscleGroupsKey: use useMuscleGroupsBigQuery()
  // - getConstantsDoc: deprecated, use specific BigQuery hooks

  getExercises: async ({ filter, organizationId } = {}) => {
    // Use the new batched approach instead of individual documents
    consola.debug("++++++ READ -> GlobalData: getExercises");
    return await getAllExercises({ filter, organizationId });
  },
  getExerciseSubscription: (id, onChange) => {
    // Immediately fetch the exercise first
    (async () => {
      try {
        consola.info(
          "++++++ READ -> GlobalData: getExerciseSubscription (immediate call)",
        );
        const exercise = await getExerciseById(id);
        if (exercise) {
          onChange(exercise);
        }
      } catch (error) {
        consola.error("Error in immediate exercise fetch:", error);
      }
    })();

    // For subsequent updates, poll at regular intervals instead of using onSnapshot
    // since we're now using batched data instead of individual documents
    const interval = setInterval(async () => {
      try {
        consola.info(
          "++++++ READ -> GlobalData: getExerciseSubscription (interval poll)",
        );
        const exercise = await getExerciseById(id);
        if (exercise) {
          onChange(exercise);
        }
      } catch (error) {
        consola.error("Error in exercise subscription polling:", error);
      }
    }, 10000); // Poll every 10 seconds

    // Return a cleanup function to cancel the interval
    return () => clearInterval(interval);
  },
  getCustomExerciseSubscription: async (id, onChange, onSubscription) => {
    consola.info(
      "++++++ SNAPSHOT -> GlobalData: getCustomExerciseSubscription",
    );
    const organizationId = getRecoil(organizationIdState);

    const customExerciseRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("customExercise")
      .doc(id);

    const subscription = customExerciseRef.onSnapshot(async (snapshot) => {
      consola.info(
        "++++++ SNAPSHOT -> GlobalData: getCustomExerciseSubscription",
      );
      const value = snapshot.data();
      onChange(value);
    });
    onSubscription(subscription);
  },
  getCustomExercisesSubscription: async (onChange, onSubscription) => {
    const organizationId = getRecoil(organizationIdState);
    if (!organizationId) {
      return null;
    }

    const customExerciseRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("customExercise");

    const subscription = customExerciseRef.onSnapshot(async (snapshot) => {
      consola.info(
        "++++++ SNAPSHOT -> GlobalData: getCustomExercisesSubscription",
      );
      onChange(snapshot);
    });
    onSubscription(subscription);
  },
  getExercisesSubscription: ({ filter, showDeleted, onChange }) => {
    // Get organization ID from Recoil state
    const organizationId = getRecoil(organizationIdState);

    // Immediately fetch exercises once
    (async () => {
      try {
        consola.info(
          "++++++ READ -> GlobalData: getExercisesSubscription (one-time fetch)",
        );
        const exercises = await getAllExercises({ filter, organizationId });

        let filteredExercises;
        // Filter out deleted exercises if not showing them
        if (!showDeleted) {
          filteredExercises = exercises.filter((ex) => !ex.isDeleted);
        } else {
          filteredExercises = exercises;
        }

        onChange(filteredExercises);
      } catch (error) {
        consola.error("Error in exercises fetch:", error);
      }
    })();

    // Return cleanup function (no interval to clean up)
    return () => {
      consola.info("Exercise subscription cleaned up");
    };
  },
  refreshExercises: async ({ filter, organizationId } = {}) => {
    try {
      consola.info("++++++ REFRESH -> GlobalData: refreshExercises");

      // Clear cache first
      const cacheKey = filter ? `globalExercises_${filter}` : "globalExercises";
      const cacheTimeKey = filter
        ? `globalExercisesTimestamp_${filter}`
        : "globalExercisesTimestamp";
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(cacheTimeKey);

      // Fetch fresh data
      const exercises = await getAllExercises({ filter, organizationId });
      return exercises;
    } catch (error) {
      consola.error("Error refreshing exercises:", error);
      return [];
    }
  },
  getExerciseKeys: async () => {
    // Use the batched approach to get all exercise keys
    const exercises = await getAllExercises();
    const groups = {};
    exercises.forEach((ex) => {
      groups[ex.id] = ex.name;
    });
    return groups;
  },
  updateExercise: async (exercise) => {
    // Use the new batched update function
    consola.info("+++++ WRITE => GlobalData: updateExercise");
    await updateExerciseInBatches(exercise);
  },
  createExerciseWithNameOnly: async ({ name }) => {
    const organizationId = getRecoil(organizationIdState);

    const id = nanoid();

    const customExerciseRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("customExercise")
      .doc(id);

    const data = {
      id: id,
      name: name,
      description: "",
      instructions: "",
      difficulty: Difficulty.beginner,
      icon: "all",
      mainMuscleGroups: ["all"],
      secondaryMuscleGroups: ["all"],
      equipment: [],
      isCustom: true,
    };
    consola.info("+++++ WRITE => GlobalData: createExerciseWithNameOnly");
    await customExerciseRef.set(data, { merge: true });
    return data;
  },
  updateCustomExercise: async ({ exercise }) => {
    const organizationId = getRecoil(organizationIdState);

    const customExerciseRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("customExercise")
      .doc(exercise.id);

    const data = {
      ...exercise,
      isCustom: true,
    };
    consola.info("+++++ WRITE => GlobalData: updateCustomExercise");
    await customExerciseRef.set(data, { merge: true });
  },
  deleteExercise: async (exercise) => {
    // Mark as deleted in custom collection and let batch updates handle it
    const organizationId = getRecoil(organizationIdState);
    const customExerciseRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("customExercise")
      .doc(exercise.id);

    await customExerciseRef.set(
      {
        ...exercise,
        isDeleted: true,
        isCustom: true,
      },
      { merge: true },
    );
  },
  checkIfCustomHasGlobal: async (exerciseId) => {
    // Check if the exercise exists in global batches
    const exercise = await getExerciseById(exerciseId);
    return exercise !== null && !exercise.isCustom;
  },
  deleteCustomExercise: async (exercise) => {
    const organizationId = getRecoil(organizationIdState);

    const customExerciseRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("customExercise")
      .doc(exercise.id);

    consola.info("+++++ WRITE => GlobalData: deleteCustomExercise");
    await customExerciseRef.set(
      {
        isDeleted: true,
      },
      { merge: true },
    );
  },
  restoreCustomExercise: async (exercise) => {
    const organizationId = getRecoil(organizationIdState);

    const customExerciseRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("customExercise")
      .doc(exercise.id);

    consola.info("+++++ WRITE => GlobalData: restoreCustomExercise");
    await customExerciseRef.set(
      {
        isDeleted: false,
      },
      { merge: true },
    );
  },
  editImageURL: async (exercise, url, index) => {
    if (index > 1) {
      index = 1;
    }
    if (index < 0) {
      index = 0;
    }

    // Get the exercise from batched data
    const exerciseData = await getExerciseById(exercise.id);

    if (exerciseData) {
      const images = exerciseData.images || [];
      const imagesCopy = [...images];

      while (imagesCopy.length < 2) {
        imagesCopy.push("");
      }

      imagesCopy[index] = url;

      // Update the exercise with the new images
      const updatedExercise = {
        ...exerciseData,
        images: imagesCopy,
      };

      consola.info("+++++ WRITE => GlobalData: editImageURL");
      await updateExerciseInBatches(updatedExercise);
    }
  },
  editCustomImageURL: async (exercise, url, index) => {
    const organizationId = getRecoil(organizationIdState);

    if (index > 1) {
      index = 1;
    }
    if (index < 0) {
      index = 0;
    }

    const customExerciseRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("customExercise")
      .doc(exercise.id);

    // First check if it exists as a custom exercise
    const customExerciseData = await customExerciseRef.get();

    if (customExerciseData.exists) {
      const customData = customExerciseData.data();
      const images = customData.images || [];
      const imagesCopy = [...images];

      while (imagesCopy.length < 2) {
        imagesCopy.push("");
      }

      imagesCopy[index] = url;

      consola.info("+++++ WRITE => GlobalData: editCustomImageURL");
      await customExerciseRef.set(
        {
          images: imagesCopy,
        },
        { merge: true },
      );
    } else {
      // If not in custom, get it from global and create it in custom
      const exerciseData = await getExerciseById(exercise.id);

      if (exerciseData) {
        const images = exerciseData.images || [];
        const imagesCopy = [...images];

        while (imagesCopy.length < 2) {
          imagesCopy.push("");
        }

        imagesCopy[index] = url;

        await customExerciseRef.set(
          {
            ...exerciseData,
            images: imagesCopy,
            isCustom: true,
          },
          { merge: true },
        );
      }
    }
  },
  addVideoURL: async (exercise, videoURL) => {
    // Get the exercise from batched data
    const exerciseData = await getExerciseById(exercise.id);

    if (exerciseData) {
      const videos = exerciseData.videos || [];
      const videosCopy = [...videos, videoURL];

      // Update the exercise with the new videos
      const updatedExercise = {
        ...exerciseData,
        videos: videosCopy,
      };

      consola.info("+++++ WRITE => GlobalData: addVideoURL");
      await updateExerciseInBatches(updatedExercise);
    }
  },
  addCustomVideoURL: async (exercise, videoURL) => {
    const organizationId = getRecoil(organizationIdState);

    const customExerciseRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("customExercise")
      .doc(exercise.id);

    // First check if it exists as a custom exercise
    const customExerciseData = await customExerciseRef.get();

    if (customExerciseData.exists) {
      const customData = customExerciseData.data();
      const videos = customData.videos || [];
      const videosCopy = [...videos, videoURL];

      consola.info("+++++ WRITE => GlobalData: addCustomVideoURL");
      await customExerciseRef.set(
        {
          videos: videosCopy,
        },
        { merge: true },
      );
    } else {
      // If not in custom, get it from global and create it in custom
      const exerciseData = await getExerciseById(exercise.id);

      if (exerciseData) {
        const videos = exerciseData.videos || [];
        const videosCopy = [...videos, videoURL];

        await customExerciseRef.set(
          {
            ...exerciseData,
            videos: videosCopy,
            isCustom: true,
          },
          { merge: true },
        );
      }
    }
  },
  deleteCustomVideoURL: async (exercise, videoURL) => {
    const organizationId = getRecoil(organizationIdState);

    const exerciseRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("customExercise")
      .doc(exercise.id);
    consola.debug("++++++ READ -> GlobalData: deleteCustomVideoURL");
    const exerciseData = await exerciseRef.get();
    if (exerciseData.exists) {
      const videos = exerciseData.data().videos;

      let videosCopy = [];
      if (videos) {
        videosCopy = _.clone(videos);
      }

      videosCopy = _.reject(videosCopy, (e) => {
        return e === videoURL;
      });
      consola.info("+++++ WRITE => GlobalData: deleteCustomVideoURL");
      exerciseRef.set(
        {
          videos: videosCopy,
        },
        { merge: true },
      );
    }
  },
  deleteVideoURL: async (exercise, videoURL) => {
    // Get the exercise from batched data
    const exerciseData = await getExerciseById(exercise.id);

    if (exerciseData) {
      const videos = exerciseData.videos || [];
      const videosCopy = videos.filter((v) => v !== videoURL);

      // Update the exercise with the filtered videos
      const updatedExercise = {
        ...exerciseData,
        videos: videosCopy,
      };

      consola.info("+++++ WRITE => GlobalData: deleteVideoURL");
      await updateExerciseInBatches(updatedExercise);
    }
  },
};

export default GlobalData;
